import React from 'react';
import { BidStrategyFormValues, BidStrategyOption } from '../types';
import { MaxCpm } from '@components/BidStrategy/MaxCpm';
import { EventPicker } from '@components/BidStrategy/EventPicker';
import { type Control } from 'react-hook-form';
import { Stack, InputAdornment } from '@mui/material';
import { RHFTextField } from '@components/form';
import { BidStrategy, BidStrategyEvent } from '@local-types/bidStrategy';

function withDisplayName<P>(
  Component: React.ComponentType<P>,
  displayName: string
): React.ComponentType<P> {
  Component.displayName = displayName;
  return Component;
}

export interface FormContext {
  control: Control<BidStrategyFormValues>;
  isMaxCPMBidEnabled: boolean;
  handleCPMSwitchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rangeText: string;
}

export const EmptyContent = withDisplayName(() => <></>, 'EmptyContent');

export const EventPickerComponent = withDisplayName<{ formContext: FormContext, bidStrategyEventSet: BidStrategyEvent[] }>(
  ({ formContext, bidStrategyEventSet }) => (
    <EventPicker
      control={formContext.control}
      bidStrategyEventSet={bidStrategyEventSet}
      fieldName="bidStrategyEvent"
    />
  ), 
  'EventPickerComponent'
);

export const TargetCpaComponent = withDisplayName<{ formContext: FormContext, bidStrategyEventSet: BidStrategyEvent[] }>(
  ({ formContext, bidStrategyEventSet }) => (
    <Stack spacing={2}>
      <EventPicker
        control={formContext.control}
        bidStrategyEventSet={bidStrategyEventSet}
        fieldName="bidStrategyEvent"
      />
      <RHFTextField
        name="bidStrategyTarget"
        label="Target Cost per Outcome"
        type="number"
        fullWidth
        size="small"
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        control={formContext.control}
      />
    </Stack>
  ), 
  'TargetCpaComponent'
);

export const MaxCpmComponent = withDisplayName<{ formContext: FormContext }>(
  ({ formContext }) => (
    <MaxCpm
      control={formContext.control}
      required={true}
      handleCPMSwitchChange={formContext.handleCPMSwitchChange}
      isMaxCPMBidEnabled={formContext.isMaxCPMBidEnabled}
      rangeText={formContext.rangeText}
      fieldNames={{
        maxCPMBidEnabled: "maxCPMBidEnabled",
        maxCPMBid: "maxCPMBid"
      }}
    />
  ), 
  'MaxCpmComponent'
);

/**
 * Transforms advertiser's bid strategies to the format expected by BulkEditModal components
 */
export const transformBidStrategies = (
  bidStrategySet?: BidStrategy[] | null,
  bidStrategyEventSet?: BidStrategyEvent[] | null,
): BidStrategyOption[] => {
  if (!bidStrategySet || bidStrategySet.length === 0) {
    return [];
  }

  const safeEventSet = bidStrategyEventSet || [];

  return bidStrategySet.map(strategy => {
    const baseOption = {
      value: String(strategy.id),
      label: strategy.display_name,
      description: strategy.description || '',
      infoText: strategy.tooltip || strategy.description || '',
    };

    // Determine if strategy should be locked
    const isLocked = strategy.display_name === 'Max Outcomes' && 
                    (!safeEventSet || safeEventSet.length === 0);

    // Define render content function (similar to getUnlockedContent in CampaignAdGroupSection)
    const renderContent = (formContext: FormContext) => {
      switch (strategy.display_name) {
        case 'Max Impressions':
        case 'Auto Bid':
        case 'Max ROAS':
          return React.createElement(EmptyContent);
        case 'Manual Bid':
        case 'Fixed CPM':
          return React.createElement(MaxCpmComponent, { formContext });
        case 'Max Outcomes':
          return React.createElement(EventPickerComponent, { 
            formContext, 
            bidStrategyEventSet: safeEventSet 
          });
        case 'Target Cost per Outcome':
          return React.createElement(TargetCpaComponent, { 
            formContext, 
            bidStrategyEventSet: safeEventSet 
          });
        default:
          return React.createElement(EmptyContent);
      }
    };

    return {
      ...baseOption,
      locked: isLocked,
      lockedInfo: isLocked ? 'This bid strategy will become available once tracking is set up.' : undefined,
      renderContent,
    };
  });
};
